import * as THREE from 'three'
import { useMemo, useState, useRef } from 'react'
import { createPortal, useFrame } from '@react-three/fiber'
import { useFBO } from '@react-three/drei'
import './shaders/simulationMaterial'
import './shaders/dofPointsMaterial'

export function Particles({ speed, fov, aperture, focus, curl, scale = 128, size = 512, conversationState = 'idle', audioData = null, ...props }) {
  const simRef = useRef()
  const renderRef = useRef()
  const lastLogTime = useRef(0)
  
  // Set up FBO
  const [scene] = useState(() => new THREE.Scene())
  const [camera] = useState(() => new THREE.OrthographicCamera(-1, 1, 1, -1, 1 / Math.pow(2, 53), 1))
  const [positions] = useState(() => new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, 1, 1, 0, -1, 1, 0]))
  const [uvs] = useState(() => new Float32Array([0, 1, 1, 1, 1, 0, 0, 1, 1, 0, 0, 0]))
  const target = useFBO(size, size, {
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    format: THREE.RGBAFormat,
    type: THREE.FloatType
  })
  
  // Normalize points
  const particles = useMemo(() => {
    const length = size * size
    const particles = new Float32Array(length * 3)
    for (let i = 0; i < length; i++) {
      let i3 = i * 3
      particles[i3 + 0] = (i % size) / size
      particles[i3 + 1] = i / size / size
    }
    return particles
  }, [size])
  
  // Update FBO and pointcloud every frame - keeping original behavior
  useFrame((state) => {
    state.gl.setRenderTarget(target)
    state.gl.clear()
    state.gl.render(scene, camera)
    state.gl.setRenderTarget(null)
    
    renderRef.current.uniforms.positions.value = target.texture
    renderRef.current.uniforms.uTime.value = state.clock.elapsedTime
    renderRef.current.uniforms.uFocus.value = THREE.MathUtils.lerp(renderRef.current.uniforms.uFocus.value, focus, 0.1)
    renderRef.current.uniforms.uFov.value = THREE.MathUtils.lerp(renderRef.current.uniforms.uFov.value, fov, 0.1)
    renderRef.current.uniforms.uBlur.value = THREE.MathUtils.lerp(renderRef.current.uniforms.uBlur.value, (5.6 - aperture) * 9, 0.1)
    
    simRef.current.uniforms.uTime.value = state.clock.elapsedTime * speed
    
    // Calculate dynamic curl based on voice frequency
    let targetCurl = curl; // Base curl from Leva controls
    
    if (audioData && audioData.frequency && conversationState === 'speaking') {
      // Map frequency to curl intensity
      // Higher frequency (audioData.frequency) = deeper voice = more curl
      // Frequency range: 0-3, maps to curl range: base to base + 0.3
      const frequencyBoost = audioData.frequency * 0.1; // 0 to 0.3 boost
      targetCurl = curl + frequencyBoost;
      
      // Clamp to reasonable curl bounds (don't go below base or above 0.5)
      targetCurl = Math.max(curl, Math.min(0.5, targetCurl));
    } else if (conversationState !== 'speaking') {
      // Return to base curl when not speaking
      targetCurl = curl;
    }
    
    simRef.current.uniforms.uCurlFreq.value = THREE.MathUtils.lerp(simRef.current.uniforms.uCurlFreq.value, targetCurl, 0.1)
    
    // Calculate dynamic scale based on audio data
    let targetScale = scale; // Base scale from Leva controls
    
    if (audioData && audioData.volume > 0 && conversationState === 'speaking') {
      // Only apply oscillation when actively speaking
      // Volume affects the amount of variance (+/- 20 from base scale)
      // Volume range: 0-1, maps to variance range: 0-20
      const volumeVariance = audioData.volume * 20;
      
      // Create oscillation pattern based on time and speech characteristics
      // Increased frequency from 2 to 4 for more responsive oscillation
      const time = state.clock.elapsedTime;
      const oscillation = Math.sin(time * 4) * volumeVariance;
      
      targetScale = scale + oscillation;
      
      // Clamp to reasonable bounds
      targetScale = Math.max(50, Math.min(300, targetScale));
    } else if (audioData && audioData.volume > 0 && conversationState !== 'speaking') {
      // During fade-out, gradually return to base scale (no oscillation)
      targetScale = scale; // Just return to base scale smoothly
      console.log('🌅 Returning to base scale during fade-out:', { 
        volume: audioData.volume.toFixed(2), 
        targetScale: targetScale.toFixed(1) 
      });
    }
    
    // Speech rate affects the lerp speed (how fast the scale changes)
    let baseLerpSpeed;
    
    if (conversationState === 'speaking' && audioData && audioData.speechRate > 0) {
      // Fast response during active speaking
      baseLerpSpeed = 0.5 + (audioData.speechRate * 0.4);
    } else if (conversationState !== 'speaking' && audioData && audioData.volume > 0) {
      // Slower, smoother return to base during fade-out
      baseLerpSpeed = 0.05 + (audioData.volume * 0.1); // Gets slower as volume decreases
    } else {
      // Default speed when no audio
      baseLerpSpeed = 0.3;
    }
    
    // For immediate response during speaking, use higher lerp speed when volume changes are significant
    const currentScale = simRef.current.uniforms.uScale.value;
    const scaleDifference = Math.abs(targetScale - currentScale);
    const adaptiveLerpSpeed = (conversationState === 'speaking' && scaleDifference > 1) 
      ? Math.min(baseLerpSpeed * 2, 0.98) 
      : baseLerpSpeed;
    
    simRef.current.uniforms.uScale.value = THREE.MathUtils.lerp(
      currentScale, 
      targetScale, 
      adaptiveLerpSpeed
    )
    
    // Debug logging for voice-reactive effects
    const now = Date.now();
    if (now - lastLogTime.current > 2000 && audioData && audioData.volume > 0) {
      console.log('🎵 Voice-Reactive Effects:', {
        baseScale: scale,
        targetScale: targetScale.toFixed(1),
        currentScale: currentScale.toFixed(1),
        baseCurl: curl.toFixed(3),
        targetCurl: targetCurl.toFixed(3),
        currentCurl: simRef.current.uniforms.uCurlFreq.value.toFixed(3),
        volume: audioData.volume.toFixed(2),
        frequency: audioData.frequency.toFixed(2),
        speechRate: audioData.speechRate.toFixed(2),
        adaptiveLerpSpeed: adaptiveLerpSpeed.toFixed(3)
      });
      lastLogTime.current = now;
    }
  })
  
  return (
    <>
      {/* Simulation goes into a FBO/Off-buffer */}
      {createPortal(
        <mesh>
          <simulationMaterial ref={simRef} />
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
            <bufferAttribute attach="attributes-uv" count={uvs.length / 2} array={uvs} itemSize={2} />
          </bufferGeometry>
        </mesh>,
        scene
      )}
      {/* The result of which is forwarded into a pointcloud via data-texture */}
      <points {...props}>
        <dofPointsMaterial ref={renderRef} />
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={particles.length / 3} array={particles} itemSize={3} />
        </bufferGeometry>
      </points>
    </>
  )
}
